import React from 'react';
import PropTypes from 'prop-types';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Link from '@material-ui/core/Link';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { toLower, get,forEach } from 'lodash';
import { navigate, Link as RouterLink } from '@reach/router';
import { Layout } from '~/Layout';
import TouchButton from '~/common/TouchButton';
import DESTINATIONS from "~/Destination/destinations";
import getCrumbs from '~/Layout/crumbs';
import WeightCalculator from '~/Summary/WeightCalculator';
import { Redirect } from '@reach/router';
import { FormControl, TextField } from '@material-ui/core';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import Modal from '~/common/Modal';


const SPECIAL_RE = /^[^=αίρετεпибść]*$/;                        
const sameIgnoringWhitespace = (a, b) => (a || '').trim() === (b || '').trim();  

function BoldAmountTitle({ text, amount }) {                    
  if (typeof text !== 'string' || !amount) return text;
  const idx = text.indexOf(amount);
  if (idx < 0) return text;
  return (
    <span style={{ display: 'inline' }}>
      {text.slice(0, idx)}
      <strong style={{ fontWeight: 700 }}>{amount}</strong>
      {text.slice(idx + amount.length)}
    </span>
  );
}

const destinationColorStyles = () => {
  const styles = {};
  forEach(DESTINATIONS, ({ slug, color }) => {
    styles[slug] = { color };
  });
  return styles;
};

const kgToLbs =  kg => kg*2.20462;

const ENTITY_THRESHOLDS_LB = Object.freeze({
  default: 350,
  54:200,
  7:396.83
});

const useStyles = makeStyles((theme) => ({
  container: {
    height: "calc(100vh - 64px)",
    overflow: "auto",
    paddingLeft: 30,
    paddingRight: 30,
    paddingTop: 20,
  },
  title: {
    textTransform: "uppercase",
    marginTop: 20,
    marginBottom: 50,
    fontSize: "3.3rem",
    [theme.breakpoints.down("sm")]: { fontSize: "2.3rem", marginTop: 10 },
  },
  text: {
    fontSize: "2.3rem",
    lineHeight: 1.4,
    [theme.breakpoints.down("sm")]: { fontSize: "1.6rem" },
  },
  smallText: {
    position: "relative",
    fontSize: "1.2rem",
    fontWeight: "bold",
    width: "40vw",
    display: "flex",
    justifyContent: "flex-start",
    [theme.breakpoints.down("sm")]: { fontSize: "0.7rem", justifyContent: "flex-start" },
  },
  bigText: {
    fontSize: "2.6rem",
    [theme.breakpoints.down("sm")]: { fontSize: "1.9rem" },
  },
  notesWrapper: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    marginTop: "4%",
    [theme.breakpoints.down("sm")]: { marginTop: "8%" },
  },
  validationNote: {
    marginTop: "0",
    fontSize: "0.8rem",
    fontWeight: "bold",
    color: "black",
    width: "40vw",
    display: "flex",
    justifyContent: "flex-end",
    [theme.breakpoints.down("sm")]: { fontSize: "0.5rem", alignItems: "center", justifyContent: "flex-end" },
  },
  textField: {
    margin: "0",
    padding: "4px",
    width: "40vw",
    height: "15vh",
    overflow: "auto",
    border: "1.5px solid black",
    borderRadius: "6px",
  },
  exceedingCharacters: {
    marginTop: "0",
    fontSize: "0.8rem",
    fontWeight: "bold",
    width: "40vw",
    display: "flex",
    justifyContent: "flex-end",
    color: "#DA5246",
    [theme.breakpoints.down("sm")]: { fontSize: "0.5rem", alignItems: "center", justifyContent: "flex-end" },
  },
  submit: {
    minHeight: 65,
    marginTop: 30,
    [theme.breakpoints.up("sm")]: { paddingRight: 40 },
    [theme.breakpoints.down("sm")]: { fontSize: "2rem" },
  },
  submitWrapper: {
    mt: 2,
    display: "inline-flex",
    justifyContent: "flex-end",
    [theme.breakpoints.up("sm")]: { position: "fixed", width: "calc(100% - 60px)", bottom: 30 },
  },
  icon: {
    fontSize: "45px !important",
    [theme.breakpoints.up("sm")]: { position: "absolute", right: 0 },
  },
  green: { color: "#347462" },
  red: { color: "#DA5246" },
  link: {
    textDecoration: "underline",
    cursor: "pointer",
  },
  ...destinationColorStyles(),
}));

const wasteTypeMessage = (wasteEntry, t) => {
  if (wasteEntry.kind === "RED_KITCHEN_WASTE") {
    return t(`wasteDescription.RED_KITCHEN_WASTE.${wasteEntry.whyTossed}`);
  }
  return t(`wasteDescription.${wasteEntry.kind}`);
};

const KG_TO_LB = 2.2046226218;
const LB_TO_KG = 1 / KG_TO_LB;
const kgToLb = (kg) => (typeof kg === 'number' ? kg * KG_TO_LB : 0);
const NOTE_MAX = 100;

const SUMMARY_MODAL_STYLES = {
  paperStyle: { maxWidth: 760, width: 640, padding: 8, paddingTop: 8 },
  contentStyle: { padding: 8 },
  actionsStyle: { paddingTop: 6, paddingBottom: 6 },
};

const Summary = ({
  saveWasteEntry,
  wasteEntry,
  appSettings: { unitSystem },
  appSettings,
}) => {
  const classes = useStyles();
  const { t, i18n } = useTranslation(["summary_page", "destination_page"]);
  const theme = useTheme();
  const xs = useMediaQuery(theme.breakpoints.down("sm"));

  const [step, setStep] = React.useState(null);
  const [note, setNote] = React.useState('');
  const [baselinePageNotes, setBaselinePageNotes] = React.useState('');

  React.useEffect(() => {
    // Save temporarily the current language to show the correct language
    // in the WasteEntry sagas showAppMessage
    localStorage.setItem("lngTemp", i18n.language);
  }, [i18n.language]);

  if (!wasteEntry || !wasteEntry.container) {
    return <Redirect to="/app" noThrow />;
  }

  const weightCalculator = new WeightCalculator(wasteEntry);
  const raw = weightCalculator.getWeight(unitSystem);
  const displayValue = `${raw.toFixed(2)} ${unitSystem === 'metric' ? 'kg' : 'lbs'}`;
  const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

  const confirmTitleStr = t('summary_page:modal_info', { amount: displayValue });     
  const noteTitleStr    = t('summary_page:modal_note_info', { amount: displayValue });

  const entityId = appSettings.entityId;
  const thresholdLb = (entityId && ENTITY_THRESHOLDS_LB[entityId] != null) ? ENTITY_THRESHOLDS_LB[entityId] : ENTITY_THRESHOLDS_LB.default;
  const rawLb = unitSystem === 'metric' ? kgToLb(raw) : raw;
  const limitStrLbs = `${thresholdLb} lbs`;


  const modalNoteTooLong = note.length >= NOTE_MAX;
  const modalNoteHasSpecials = !SPECIAL_RE.test(note);
  const modalHelperText = note.length >= NOTE_MAX
  ? t('summary_page:maxCharacters')
  : modalNoteHasSpecials
    ? t('summary_page:specialCharacters')
    : `${note.length}/${NOTE_MAX}`;

const modalSubmitDisabled =
  (note.trim().length === 0) ||
  sameIgnoringWhitespace(note, baselinePageNotes) ||
  modalNoteTooLong ||
  modalNoteHasSpecials;

    const metricLimitBracket =
  unitSystem === 'metric'
    ? ` (${t('weight_limit_note', 'weight limit exceeded')}: ${(thresholdLb / 2.2046226218).toFixed(2)} kg)`
    : '';

    const submit = (pageNotes) => {
      const roundedLb = round2(rawLb); 
    
      if (roundedLb >= thresholdLb) {
        setBaselinePageNotes(pageNotes || '');
        setNote(function(prev){ if (prev && prev.length) return prev; return pageNotes || ''; });
        setStep('confirm');
        return;
      }
    
      saveWasteEntry(pageNotes || '');
      navigate("/app");
    };
    

  const handleConfirmYes = function(currentPageNotes) {
    const next = (currentPageNotes || '');
    setBaselinePageNotes(next);
    setNote(next);
    setStep('note');
  };

  const handleConfirmNo = () => {
    navigate('/container', { state: { showInfoModal: true } });
    setStep(null);
  };

  const colorForWasteTypeMessage =
    wasteEntry.kind === "TRIM_BONES_SHELLS" ? "green" : "red";

  // todo: after submit, if you go back, this will error because we clear out the wasteEntry
  // should do a quick representative check and redirect to home if it fails

  return wasteEntry.container ? (
    <Layout
      crumbs={getCrumbs("home/kindOfWaste/whyTossed/destination/container/quantity/typeOfWaste/summary")}
      previousScreen="/typeOfWaste"
    >
      <Formik
        initialValues={{ notes: "" }}
        validationSchema={Yup.object().shape({
          notes: Yup.string()
            .max(100, t('summary_page:maxCharacters'))
            .matches(SPECIAL_RE, t('summary_page:specialCharacters')),
        })}
        onSubmit={(values) => submit(values.notes)}
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
          <Form onSubmit={handleSubmit}>
            <Box className={classes.container}>
              <Typography variant="h2" align="center" className={classes.title} gutterBottom>
                {t("summary_page:title")}
              </Typography>

              <Typography align="center" gutterBottom className={classes.text}>
                {t("summary_page:record")}{" "}
                <Link
                  component={RouterLink}
                  to={wasteEntry.quantity.type === "exact" ? "/exactQuantity" : "/typeOfWaste"}
                  className={clsx(classes.bigText, classes.link)}
                >
                  {weightCalculator.getWeight(unitSystem).toFixed(2)}{" "}
                  {unitSystem === "metric" ? "kg" : "lbs"}
                </Link>
              </Typography>

              <Typography align="center" gutterBottom className={classes.text}>
                {t("summary_page:of")}{" "}
                <Link
                  component={RouterLink}
                  to="/kindOfWaste"
                  className={clsx(classes.bigText, classes.link, classes[colorForWasteTypeMessage])}
                >
                  {wasteTypeMessage(wasteEntry, t)}
                </Link>
              </Typography>

              <Typography align="center" gutterBottom className={classes.text}>
                {t("summary_page:going")}{" "}
                <Link
                  component={RouterLink}
                  to="/destination"
                  className={clsx(classes.bigText, classes.link, classes[wasteEntry.destination])}
                >
                  {toLower(t(`destination_page:buttons.${wasteEntry.destination}.title`))}
                </Link>
                .
              </Typography>

              <Box className={classes.notesWrapper}>
                <Typography className={classes.smallText}>
                  {t("summary_page:noteLabel")}
                </Typography>
                <FormControl>
                  <TextField
                    id="notes"
                    name="notes"
                    InputProps={{ disableUnderline: true }}
                    className={classes.textField}
                    multiline
                    placeholder={t("summary_page:placeholder")}
                    value={values.notes.slice(0, 100)}
                    onChange={(e) => {
                      if (e.target.value.length <= 100) { handleChange(e); }
                    }}
                    onBlur={handleBlur}
                    error={Boolean(errors.notes)}
                    onPaste={(e) => {
                      e.preventDefault();
                      const pastedText = e.clipboardData.getData("text/plain");
                      const truncatedText = pastedText.substring(0, 100);
                      const remainingChars = 100 - values.notes.length;
                      const newNotes =
                        values.notes.substring(0, e.target.selectionStart) +
                        truncatedText.substring(0, remainingChars) +
                        values.notes.substring(e.target.selectionEnd);
                      handleChange({ target: { name: "notes", value: newNotes } });
                    }}
                  />
                </FormControl>
                <Typography
                  className={clsx(
                    classes.validationNote,
                    values.notes.length >= 100 && classes.exceedingCharacters
                  )}
                >
                  {errors.notes && touched.notes
                    ? errors.notes
                    : values.notes.length >= 100
                    ? t("summary_page:maxCharacters")
                    : `${values.notes.length}/100`}
                </Typography>
              </Box>

              <Box className={classes.submitWrapper}>
                <TouchButton onClick={() => {}} fullWidth={xs} className={classes.submit} type="submit">
                  {t("summary_page:submit")}
                  <ChevronRightIcon className={classes.icon} />
                </TouchButton>
              </Box>
            </Box>

            <Modal
              open={step === 'confirm'}
              onClose={() => setStep(null)}
              title={<BoldAmountTitle text={confirmTitleStr} amount={displayValue} />}
              paperStyle={SUMMARY_MODAL_STYLES.paperStyle}
              contentStyle={SUMMARY_MODAL_STYLES.contentStyle}
              actionsStyle={SUMMARY_MODAL_STYLES.actionsStyle}
              actions={[
                { label: t('summary_page:modal_yes'), onClick: function(){ handleConfirmYes(values.notes); }, variant: 'primary', type: 'button',width: 358 },
                { label: t('summary_page:modal_no'),  onClick: handleConfirmNo, variant: 'secondary', type: 'button', width: 358 },
              ]}
            >
            </Modal>

            <Modal
              open={step === 'note'}
              onClose={() => setStep(null)}
              title={<BoldAmountTitle text={noteTitleStr} amount={displayValue} />}
              textareaProps={{
                placeholder: t('summary_page:placeholder'),
                value: note,
                onChange: function(e){ setNote(e.target.value.slice(0, 100)); },
                rows: 4,
                error: false,
                helperText: modalHelperText,
                FormHelperTextProps: { style: { fontSize: '0.9rem', lineHeight: 1.1, marginTop: 5, marginLeft: 0, color:  modalNoteTooLong || modalNoteHasSpecials ? '#DA5246' : 'rgba(0,0,0,0.54)' } },
              }}
              actions={[
                {
                  label: t('summary_page:modal_submit_entry'),
                  onClick: function(){
                    if (modalSubmitDisabled) return;
                    saveWasteEntry(note);
                    navigate('/app');
                  },
                  variant: 'primary',
                  type: 'button',
                  disabled: modalSubmitDisabled,
                },
              ]}
              actionsStyle={{ marginTop: 4 }}  
            />
          </Form>
        )}
      </Formik>
    </Layout>
      ) : (
        <Redirect to="/app" noThrow />
  );
};

Summary.defaultProps = { wasteEntry: "" };

Summary.propTypes = {
  appSettings: PropTypes.shape({
    unitSystem: PropTypes.oneOf(["imperial", "metric"]).isRequired,
    kitchens: PropTypes.oneOfType([
      PropTypes.shape({ entity_id: PropTypes.string }),
      PropTypes.array,
    ]),
  }).isRequired,
  wasteEntry: PropTypes.shape({
    destination: PropTypes.string.isRequired,
    quantity: PropTypes.shape({
      type: PropTypes.oneOf(["percentage", "exact"]),
      amount: PropTypes.number,
      units: PropTypes.string,
      percentage: PropTypes.number,
      count: PropTypes.number,
    }).isRequired,
    whyTossed: PropTypes.string,
    kind: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    container: PropTypes.any,
  }),
  saveWasteEntry: PropTypes.func.isRequired,
};

export default Summary;
